import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TravelStats from './components/TravelStats';
import TravelRequestQueue from './components/TravelRequestQueue';
import BookingStatusPanel from './components/BookingStatusPanel';
import ComplianceIndicators from './components/ComplianceIndicators';
import NotificationPanel from '../../components/ui/NotificationPanel';
import EscalationMonitor from './components/EscalationMonitor';
import TravelPolicyAssistant from './components/TravelPolicyAssistant';
import { useAuth } from '../../contexts/AuthContext';

const TravelManagementDashboard = () => {
  const { user } = useAuth();
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  // Mock travel statistics
  const travelStats = {
    activeTravelRequests: 23,
    upcomingTrips: 15,
    budgetUtilization: 68,
    complianceIssues: 3,
    pendingApprovals: 8,
    activeBookings: 42
  };

  // Mock travel requests data
  const mockTravelRequests = [
    {
      id: 1,
      requestNumber: 'TR-2026-001',
      travelerName: 'Sarah Johnson',
      department: 'Engineering',
      destination: 'London, UK',
      travelType: 'international',
      startDate: '2026-02-15',
      endDate: '2026-02-20',
      purpose: 'Client meeting and technical workshop',
      estimatedBudget: 3500,
      status: 'approved',
      priority: 'high',
      complianceStatus: 'compliant'
    },
    {
      id: 2,
      requestNumber: 'TR-2026-002',
      travelerName: 'Michael Chen',
      department: 'Marketing',
      destination: 'New York, USA',
      travelType: 'domestic',
      startDate: '2026-03-01',
      endDate: '2026-03-05',
      purpose: 'Marketing conference attendance',
      estimatedBudget: 2200,
      status: 'pending_approval',
      priority: 'normal',
      complianceStatus: 'pending_review'
    },
    {
      id: 3,
      requestNumber: 'TR-2026-003',
      travelerName: 'Emily Rodriguez',
      department: 'Operations',
      destination: 'Tokyo, Japan',
      travelType: 'international',
      startDate: '2026-04-10',
      endDate: '2026-04-17',
      purpose: 'Partner facility inspection',
      estimatedBudget: 4800,
      status: 'draft',
      priority: 'normal',
      complianceStatus: 'compliant'
    },
    {
      id: 4,
      requestNumber: 'TR-2026-004',
      travelerName: 'David Kim',
      department: 'Sales',
      destination: 'Berlin, Germany',
      travelType: 'international',
      startDate: '2026-02-28',
      endDate: '2026-03-03',
      purpose: 'Sales presentation and contract negotiation',
      estimatedBudget: 2800,
      status: 'pending_approval',
      priority: 'high',
      complianceStatus: 'violation'
    }
  ];

  // Mock bookings data
  const mockBookings = [
    {
      id: 1,
      requestNumber: 'TR-2026-001',
      bookingType: 'flight',
      vendor: 'British Airways',
      reference: 'BA-2026-4521',
      travelDate: '2026-02-15',
      status: 'confirmed',
      cost: 1200
    },
    {
      id: 2,
      requestNumber: 'TR-2026-001',
      bookingType: 'hotel',
      vendor: 'Hilton London',
      reference: 'HIL-LON-8821',
      travelDate: '2026-02-15',
      status: 'confirmed',
      cost: 1500
    },
    {
      id: 3,
      requestNumber: 'TR-2026-002',
      bookingType: 'flight',
      vendor: 'Delta Airlines',
      reference: 'DL-2026-7732',
      travelDate: '2026-03-01',
      status: 'pending',
      cost: 450
    }
  ];

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 1000);
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
  };

  const handleQuickAction = (actionId) => {
    console.log('Quick action:', actionId);
  };

  const breadcrumbItems = [
    { label: 'Roamsure', path: '/travel-management-dashboard' },
    { label: 'Travel Management', path: '/travel-management-dashboard' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 px-6 pb-8">
        {/* Breadcrumb */}
        <Breadcrumb items={breadcrumbItems} />

        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Travel Management Dashboard
            </h1>
            <p className="text-muted-foreground">
              Manage travel requests, bookings, and compliance across your organization
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
            >
              New Travel Request
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
            >
              Export Report
            </Button>
          </div>
        </div>

        {/* Travel Statistics */}
        <TravelStats stats={travelStats} />

        {/* Filter Bar */}
        <div className="glass-container rounded-xl border border-border/50 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-muted-foreground">Filter by:</span>
              <div className="flex items-center space-x-2">
                {['all', 'pending_approval', 'approved', 'active', 'completed']?.map((filter) => (
                  <button
                    key={filter}
                    onClick={() => handleFilterChange(filter)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      selectedFilter === filter
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {filter?.replace('_', ' ')?.charAt(0)?.toUpperCase() + filter?.slice(1)?.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Icon name="Search" size={16} className="text-muted-foreground" />
              <input
                type="text"
                placeholder="Search travel requests..."
                className="bg-transparent border-none outline-none text-sm text-foreground placeholder:text-muted-foreground"
              />
            </div>
          </div>
        </div>

        {/* Main Dashboard Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Stats and Requests */}
          <div className="lg:col-span-2 space-y-6">
            <EscalationMonitor />
            <TravelRequestQueue requests={mockTravelRequests} />
            <BookingStatusPanel bookings={mockBookings} />
          </div>

          {/* Right Column - AI Assistant, Compliance and Notifications */}
          <div className="space-y-6">
            <TravelPolicyAssistant />
            <NotificationPanel maxItems={6} showFilters={true} userId={user?.id} />
            <ComplianceIndicators requests={mockTravelRequests} />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="glass-container rounded-xl border border-border/50 mt-6">
          <div className="p-6 border-b border-border/50">
            <h3 className="text-lg font-semibold text-foreground">Quick Actions</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { id: 'emergency-travel', title: 'Emergency Travel', icon: 'AlertTriangle', color: 'error' },
                { id: 'bulk-approval', title: 'Bulk Approval', icon: 'CheckCircle', color: 'success' },
                { id: 'policy-review', title: 'Policy Review', icon: 'FileText', color: 'warning' },
                { id: 'vendor-coordination', title: 'Vendor Coordination', icon: 'Users', color: 'secondary' }
              ]?.map((action) => (
                <button
                  key={action?.id}
                  onClick={() => handleQuickAction(action?.id)}
                  className={`p-4 rounded-lg border transition-all duration-200 text-left hover:shadow-md text-${action?.color} bg-${action?.color}/10 border-${action?.color}/20 hover:bg-${action?.color}/20`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center border mb-3 text-${action?.color} bg-${action?.color}/10 border-${action?.color}/20`}>
                    <Icon name={action?.icon} size={20} />
                  </div>
                  <h4 className="font-medium text-foreground">{action?.title}</h4>
                </button>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TravelManagementDashboard;